import { h, JSX, FunctionalComponent } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { TooltipTailLeftIcon } from '@uw/icons';
import { keyUtil } from '@uw/utils';
import './tooltip.scss';
import { generateId } from '../../utils';

export interface TooltipProps {
  title: string;
  icon: JSX.Element;
  ariaLabel?: string;
}

const INACTIVE_TOOLTIP_DELAY = 300;
const CLOSE_BY_ESC_KEY_DELAY = 100;

export const Tooltip: FunctionalComponent<TooltipProps> = ({
  title,
  ariaLabel,
  icon,
  children,
}) => {
  const [id] = useState(generateId());
  const [isOpened, setIsOpened] = useState(false);
  const [allowOpenOnFocus, setAllowOpenOnFocus] = useState(true);
  const mouseOutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipContainer = useRef<HTMLSpanElement>(null);
  const tooltipBtn = useRef<HTMLSpanElement>(null);

  const toggleTooltip = (open: boolean, focusTooltipBtn?: boolean) => {
    setIsOpened(open);

    if (focusTooltipBtn && tooltipBtn.current) {
      tooltipBtn.current.focus();
    }

    // sends post message to track if any tooltip is open or closed
    window.parent.postMessage(
      {
        isUserWay: true,
        type: 'widgetTooltipToggle',
        action: open ? 'widgetTooltipOpen' : 'widgetTooltipClose',
      },
      '*',
    );
  };

  const onCloseInit = () => {
    mouseOutTimer.current && clearTimeout(mouseOutTimer.current);
    if (tooltipContainer.current && !tooltipContainer.current.contains(document.activeElement)) {
      mouseOutTimer.current = setTimeout(() => {
        toggleTooltip(false);
      }, INACTIVE_TOOLTIP_DELAY);
    }
  };

  const enableTooltip = () => {
    mouseOutTimer.current && clearTimeout(mouseOutTimer.current);
    toggleTooltip(true);
  };

  const handleFocusIn = () => {
    if (allowOpenOnFocus) {
      enableTooltip();
    } else {
      setAllowOpenOnFocus(true);
    }
  };

  const handleFocusOut = (e: FocusEvent) => {
    if (
      tooltipContainer.current &&
      !tooltipContainer.current.contains(e.relatedTarget as HTMLElement)
    ) {
      toggleTooltip(false);
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (
      (keyUtil.isEnter(e) || keyUtil.isSpace(e)) &&
      e.target instanceof HTMLElement &&
      e.target.matches('.uwaw-main-tooltip-icon')
    ) {
      // pressing Enter/Space on tooltip button, opens the tooltip
      e.preventDefault();
      toggleTooltip(true);
    } else if (keyUtil.isEscape(e)) {
      // pressing Esc on the tooltip button or inside the tooltip, closes the tooltip
      const isFocusInsideTooltipBox =
        document.activeElement !== tooltipBtn.current &&
        tooltipContainer.current?.contains(document.activeElement);

      if (isFocusInsideTooltipBox) {
        setAllowOpenOnFocus(false);
      }

      // note: without delay, it would first close tooltip, then close the widget, because
      // there is no visible tooltip
      setTimeout(() => {
        toggleTooltip(false, isFocusInsideTooltipBox);
      }, CLOSE_BY_ESC_KEY_DELAY);
    }
  };

  return (
    <span
      ref={tooltipContainer}
      onMouseOut={onCloseInit}
      onMouseOver={enableTooltip}
      onFocusOut={handleFocusOut}
      onKeyDown={handleKeydown}
      className="uwaw-main-tooltip-container"
    >
      <span
        ref={tooltipBtn}
        id={`uwaw-tooltip-icon-${id}`}
        tabIndex={0}
        role="button"
        aria-label={ariaLabel}
        aria-describedby={`uwaw-tooltip-${id}`}
        className="uwaw-main-tooltip-icon"
        onFocus={handleFocusIn}
        onMouseOver={enableTooltip}
      >
        {icon}
      </span>
      <span
        id={`uwaw-tooltip-${id}`}
        role="tooltip"
        className={`uwaw-main-tooltip ${!isOpened ? 'uwaw-main-tooltip__hidden' : ''}`}
      >
        <TooltipTailLeftIcon
          svgAttrs={{ 'aria-hidden': 'true' }}
          className="uwaw-main-tooltip__tail"
        />
        <span className="uwaw-main-tooltip__i">
          <span className="uwaw-main-tooltip__title">{title}</span>
          {children}
        </span>
      </span>
    </span>
  );
};
